import React, { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ThemeToggle from './ThemeToggle';
import { ERROR_RECOVERY_TYPES } from '../../config/errorRecoveryTypes';
import { useNavigation } from '../../hooks/useNavigation';
import '../../styles/errorRecovery.css';

function HubErrorPage({
  type,
  variant = 'shell',
  supportCode = null,
  onRetry = null,
  showBack = true,
  showDashboard = true,
  showRetry = true,
  secondaryAction = null,
  devDetails = null,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getTenantPath, navigateToTenant } = useNavigation();
  const focusRef = useRef(null);

  const config = ERROR_RECOVERY_TYPES[type] || ERROR_RECOVERY_TYPES.unexpected;
  const { icon, severity, actions, titleKey, descriptionKey, hintKey } = config;

  useEffect(() => {
    focusRef.current?.focus();
  }, []);

  // `useLayoutEffect`, não `useEffect`: efeito passivo roda depois da pintura,
  // e 21 das 30 regras de `errorRecovery.css` são escopadas nesta classe. Sem
  // isso a página de erro aparece como markup cru por um quadro. Mesma
  // correção que o `AppShell` recebeu.
  //
  // O foco continua num efeito passivo logo acima, de propósito: mover foco
  // antes da pintura é o tipo de coisa que atrapalha, e ali o atraso é
  // desejado.
  useLayoutEffect(() => {
    if (variant !== 'standalone') {
      return undefined;
    }
    document.body.classList.add('dashboard-shell');
    return () => {
      document.body.classList.remove('dashboard-shell');
    };
  }, [variant]);

  const handleRetry = useCallback(() => {
    if (onRetry) {
      onRetry();
      return;
    }
    window.location.reload();
  }, [onRetry]);

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigateToTenant('');
  }, [navigate, navigateToTenant]);

  const handleDashboard = useCallback(() => {
    navigate(getTenantPath(''));
  }, [getTenantPath, navigate]);

  const content = (
    <section
      className="section hub-error-page"
      role="alert"
      aria-live="assertive"
      tabIndex={-1}
      ref={focusRef}
    >
      <div className="hub-error-page__card">
        <div className={`hub-error-page__icon hub-error-page__icon--${severity}`}>
          <span className="material-symbols-rounded" aria-hidden="true">{icon}</span>
        </div>

        <strong className="hub-error-page__title">{t(titleKey)}</strong>
        <small className="hub-error-page__description">{t(descriptionKey)}</small>

        {hintKey ? (
          <p className="hub-error-page__hint">{t(hintKey)}</p>
        ) : null}

        {supportCode ? (
          <div className="hub-error-page__support-code">
            <span className="hub-error-page__support-code-label">
              {t('errors.recovery.supportCodeLabel')}
            </span>
            <code>{supportCode}</code>
            <small className="hub-error-page__support-code-hint">
              {t('errors.recovery.supportCodeHint')}
            </small>
          </div>
        ) : null}

        <div className="hub-error-page__actions">
          {actions.retry && showRetry ? (
            <button type="button" className="btn primary" onClick={handleRetry}>
              {t('errors.recovery.actions.retry')}
            </button>
          ) : null}

          {actions.back && showBack ? (
            <button type="button" className="btn outline" onClick={handleBack}>
              {t('errors.recovery.actions.back')}
            </button>
          ) : null}

          {actions.dashboard && showDashboard ? (
            <button type="button" className="btn outline" onClick={handleDashboard}>
              {t('errors.recovery.actions.dashboard')}
            </button>
          ) : null}

          {secondaryAction ? (
            <RouterLink to={secondaryAction.to} className="btn outline">
              {t(secondaryAction.labelKey)}
            </RouterLink>
          ) : null}
        </div>

        {devDetails ? (
          <details className="hub-error-dev-details">
            <summary>{devDetails.summary}</summary>
            <pre>{devDetails.body}</pre>
          </details>
        ) : null}
      </div>
    </section>
  );

  if (variant === 'standalone') {
    return (
      <div className="hub-error-standalone hub360-auth">
        <div className="hub-error-standalone__theme">
          <ThemeToggle />
        </div>
        {content}
      </div>
    );
  }

  return content;
}

HubErrorPage.propTypes = {
  type: PropTypes.oneOf([
    'unexpected',
    'server',
    'forbidden',
    'offline',
    'timeout',
    'not_found',
  ]).isRequired,
  variant: PropTypes.oneOf(['shell', 'standalone']),
  supportCode: PropTypes.string,
  onRetry: PropTypes.func,
  showBack: PropTypes.bool,
  showDashboard: PropTypes.bool,
  showRetry: PropTypes.bool,
  secondaryAction: PropTypes.shape({
    to: PropTypes.string.isRequired,
    labelKey: PropTypes.string.isRequired,
  }),
  devDetails: PropTypes.shape({
    summary: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
  }),
};

export default HubErrorPage;
